import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import Home from "./pages/Home";
import { AuthProvider } from "./hooks/useAuth";
import { LocaleProvider } from "@/i18n/LocaleProvider";

// Lazy-load non-critical routes to shrink the initial JS bundle
// and reduce main-thread blocking time / Max Potential FID.
const Subjects = lazy(() => import("./pages/Subjects"));
const SubjectDetail = lazy(() => import("./pages/SubjectDetail"));
const Tutorials = lazy(() => import("./pages/Tutorials"));
const FAQ = lazy(() => import("./pages/FAQ"));
const About = lazy(() => import("./pages/About"));
const Mission = lazy(() => import("./pages/Mission"));
const Materiale = lazy(() => import("./pages/Materiale"));
const Pricing = lazy(() => import("./pages/Pricing"));
const AdsLanding = lazy(() => import("./pages/AdsLanding"));
const BetaAdsLanding = lazy(() => import("./pages/BetaAdsLanding"));
const EducatorsLanding = lazy(() => import("./pages/EducatorsLanding"));
const Community = lazy(() => import("./pages/Community"));
const QuestionDetail = lazy(() => import("./pages/QuestionDetail"));
const News = lazy(() => import("./pages/News"));

const ModuleOverview = lazy(() => import("./pages/ModuleOverview"));
const Lesson = lazy(() => import("./pages/Lesson"));
const Auth = lazy(() => import("./pages/Auth"));
const CheckEmail = lazy(() => import("./pages/CheckEmail"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const OnboardingBilling = lazy(() => import("./pages/OnboardingBilling"));
const AccountBilling = lazy(() => import("./pages/AccountBilling"));
const Dev = lazy(() => import("./pages/Dev"));
const Privacy = lazy(() => import("./pages/legal/Privacy"));
const Terms = lazy(() => import("./pages/legal/Terms"));
const Returns = lazy(() => import("./pages/legal/Returns"));
const Contact = lazy(() => import("./pages/Contact"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));

const queryClient = new QueryClient();

// Localized routes are mounted under both `/` (RO, default) and `/en` (EN).
// Components read the active locale from LocaleProvider, which derives it
// from the URL prefix.
const AppRoutes = () => (
  <Routes>
    {(["/", "/en"] as const).flatMap((base) => {
      const p = (path: string) => `${base === "/" ? "" : base}${path}`;
      return [
        <Route key={`${base}-home`} path={base} element={<Home />} />,
        <Route key={`${base}-subjects`} path={p("/subjects")} element={<Subjects />} />,
        <Route key={`${base}-subject`} path={p("/subjects/:slug")} element={<SubjectDetail />} />,
        <Route key={`${base}-module`} path={p("/subjects/romanian/module/:moduleSlug")} element={<ModuleOverview />} />,
        <Route key={`${base}-lesson`} path={p("/subjects/romanian/module/:moduleSlug/lesson/:lessonId")} element={<Lesson />} />,
        <Route key={`${base}-tutorials`} path={p("/tutorials")} element={<Tutorials />} />,
        <Route key={`${base}-faq`} path={p("/faq")} element={<FAQ />} />,
        <Route key={`${base}-about`} path={p("/about")} element={<About />} />,
        <Route key={`${base}-mission`} path={p("/misiune")} element={<Mission />} />,
        <Route key={`${base}-mission-en`} path={p("/mission")} element={<Mission />} />,
        <Route key={`${base}-materiale`} path={p("/materiale")} element={<Materiale />} />,
        <Route key={`${base}-materials-en`} path={p("/materials")} element={<Materiale />} />,
        <Route key={`${base}-resurse`} path={p("/resurse")} element={<Navigate to={`${base === "/" ? "" : base}/materiale?tab=biblioteca`} replace />} />,
        <Route key={`${base}-pricing`} path={p("/pricing")} element={<Pricing />} />,
        <Route key={`${base}-start`} path={p("/start")} element={<AdsLanding />} />,
        <Route key={`${base}-bac`} path={p("/bac")} element={<AdsLanding />} />,
        <Route key={`${base}-beta`} path={p("/beta")} element={<BetaAdsLanding />} />,
        <Route key={`${base}-profesori`} path={p("/profesori")} element={<EducatorsLanding />} />,
        <Route key={`${base}-alumni`} path={p("/alumni")} element={<EducatorsLanding />} />,
        <Route key={`${base}-comunitate-educatori`} path={p("/comunitate-educatori")} element={<EducatorsLanding />} />,
        <Route key={`${base}-educators-en`} path={p("/educators")} element={<EducatorsLanding />} />,
        <Route key={`${base}-community`} path={p("/community")} element={<Community />} />,
        <Route key={`${base}-question`} path={p("/community/:id")} element={<QuestionDetail />} />,
        <Route key={`${base}-news`} path={p("/news")} element={<News />} />,
        <Route key={`${base}-bac-arhiva`} path={p("/bac-arhiva")} element={<Navigate to={`${base === "/" ? "" : base}/materiale?tab=arhiva`} replace />} />,
        <Route key={`${base}-arhiva`} path={p("/arhiva")} element={<Navigate to={`${base === "/" ? "" : base}/materiale?tab=arhiva`} replace />} />,
        <Route key={`${base}-auth`} path={p("/auth")} element={<Auth />} />,
        <Route key={`${base}-check`} path={p("/auth/check-email")} element={<CheckEmail />} />,
        <Route key={`${base}-reset`} path={p("/reset-password")} element={<ResetPassword />} />,
        <Route key={`${base}-dashboard`} path={p("/dashboard")} element={<Dashboard />} />,
        <Route key={`${base}-onboard`} path={p("/onboarding/billing")} element={<OnboardingBilling />} />,
        <Route key={`${base}-billing`} path={p("/account/billing")} element={<AccountBilling />} />,
        <Route key={`${base}-dev`} path={p("/dev")} element={<Dev />} />,
        <Route key={`${base}-privacy`} path={p("/legal/confidentialitate")} element={<Privacy />} />,
        <Route key={`${base}-terms`} path={p("/legal/termeni")} element={<Terms />} />,
        <Route key={`${base}-returns`} path={p("/legal/returnare")} element={<Returns />} />,
        <Route key={`${base}-privacy-en`} path={p("/legal/privacy")} element={<Privacy />} />,
        <Route key={`${base}-terms-en`} path={p("/legal/terms")} element={<Terms />} />,
        <Route key={`${base}-returns-en`} path={p("/legal/returns")} element={<Returns />} />,
        <Route key={`${base}-contact`} path={p("/contact")} element={<Contact />} />,
      ];
    })}
    <Route path="*" element={<NotFound />} />
  </Routes>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider
      attribute="class"
      defaultTheme="system"
      enableSystem
      storageKey="se-theme"
      disableTransitionOnChange
    >
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <LocaleProvider>
            <Suspense fallback={null}>
              <AppRoutes />
            </Suspense>
          </LocaleProvider>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
